import React, {useState} from 'react';
import {Button, Row, Col, Form, Badge} from 'react-bootstrap';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {compose} from '../../lib/compose';
import {usePortalContext} from '../../lib/context/portalContext';
import {useDeviceMutations} from '../devices/deviceMutations';
import withAlerts from '../../lib/withAlerts';
import {nullCheck} from '../../lib/testUUID';
import {NIL} from 'uuid';
import {useForm} from 'react-hook-form';
import {Can} from '../auth/can';

const PolicyAssignment = props => {
  const {success, error} = props.alerts;
  const {rowData, togglePortal} = usePortalContext();
  const [showAssignment, setShowAssignment] = useState(null);
  const {register, handleSubmit, getValues, watch, formState: {errors}} = useForm();
  const assignedPolicyId = rowData?.policy_id ?? rowData?.policy?.policy_id ?? null;
  const greyhound_family = rowData?.product_type?.greyhound_family;
  const pID = nullCheck(assignedPolicyId);
  const {data: pData, loading} = useQuery(POLICIES);
  const policies = pData?.Policies?.result ?? [];  
  
  const {setCriteria} = useDeviceMutations();
  const hasDevice = rowData?.serial_number ? true : false;
  const hasUser = rowData?.user?.id ? true : false;
  
  const assignmentSubmit = async (formData) => {
    const value = formData === 'unassign' ? NIL : formData.policy_selector;
    const msg = formData.policy_selector ? 'assigned' : 'unassigned';

    const setDeviceCriteria = await setCriteria({variables: 
      {
        serial_number: rowData?.serial_number,
        field: 'policy_id',
        value
      }
    });
    
    if(setDeviceCriteria.errors) {
      error({msg: 'There was an error assigning the policy. Please try again.'});
    } else {
      // Policy assigned / Policy unassigned
      success({msg: `Policy ${msg}.`});
    }
    togglePortal();  
  }


  return (
    <div className="offcanvas-form">
      
      <Form className="mb-3" onSubmit={handleSubmit(assignmentSubmit)}>
        {!hasDevice || !hasUser && 
          <Row>
            <Col>
              <p>Device must be paired to user before policy can be assigned.</p>   
            </Col>
          </Row> 
        }
        {pID ?
        <>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Policy Name</Form.Label>
                <div>{rowData?.policy?.name}</div>
              </Form.Group>
              
              <Form.Group className="mb-3 rules-pills">
                <Form.Label>Policy Rules</Form.Label>
                {rowData?.policy?.has_battery_warning &&  
                <Row>
                  <Col className="mb-3">
                    <Badge pill bg="none">Minimum Battery Level: <span className="fw-bold">{rowData?.policy?.battery_warning_threshold}%</span></Badge>   
                  </Col>
                </Row>
                }

                {rowData?.policy?.is_phone_protected &&
                  <Row>
                    <Col className="mb-3">
                      <Badge pill bg="none">Protected Mode: <span className="fw-bold">ON</span></Badge>
                    </Col>
                  </Row>         
                }
                {rowData?.policy?.is_cellular_connected &&
                  <Row>
                    <Col className="mb-3">
                      <Badge pill bg="none">Cellular Radio: <span className="fw-bold">OFF</span></Badge>
                    </Col>
                  </Row>         
                }

                {rowData?.policy?.is_phone_mismatch &&
                  <Row>
                    <Col className="mb-3">
                      <Badge pill bg="none">Phone Bonding Mismatch: <span className="fw-bold">ON</span></Badge>
                    </Col>
                  </Row>         
                }
              </Form.Group>
            </Col>
          </Row>
          {/* RBAC: SAFECASE_POLICY_ASSIGNMENT gates only these change/
              unassign actions - viewing the policy name/rules above stays
              visible under SAFECASE_VIEW. */}
          <Can code="SAFECASE_POLICY_ASSIGNMENT">
            <Row className="mb-3">
              <Col>
                <span className="link-span privoro-blue" onClick={() => setShowAssignment(true)}>Change Assigned Policy</span>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <span className="link-span" onClick={() => assignmentSubmit('unassign')}>Unassign Policy</span>
              </Col>
            </Row>
          </Can>
        </>
        : 
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Policy Name</Form.Label>
                <div>No Policy Assigned</div>
              </Form.Group>
            </Col>
          </Row>
        }

        {/* RBAC: SAFECASE_POLICY_ASSIGNMENT also gates this block directly -
            when no policy is assigned yet (!pID), this shows immediately
            with no trigger link, unlike hdmDetails.jsx's equivalent, so a
            gate on a single trigger link wouldn't be sufficient here. */}
        {(!pID || showAssignment) &&
        <Can code="SAFECASE_POLICY_ASSIGNMENT">
          {greyhound_family &&
          <Row>
            <Col>
              <p><strong>Notice: </strong> SafeCase ONX does not support policy application & monitoring. For further information and assistance, please reach out to your designated Privoro representative.</p>
            </Col>
          </Row>
          }
          <Row>
            <Col className="mb-3">
              <Form.Label>Assign Policy</Form.Label>
              <Form.Select {...register('policy_selector')} disabled={ (!hasDevice || !hasUser || greyhound_family) }>
                <option>Select policy</option>
                {policies.map(({id, name}, i) => (
                  <option key={i} value={id}>{name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="d-grid gap-2">
                <Button type="submit" disabled={greyhound_family || !hasDevice || watch('policy_selector') === undefined || watch('policy_selector') === 'Select policy'}>Assign Policy</Button>
              </div>
            </Col>
          </Row>
        </Can>
        }
      </Form>      
    </div>
  )
}

const POLICIES = gql`
  query getPolicies {
    Policies {
      result {
        id
        name
      }      
    }
  }
`;

export default compose(
  withAlerts
)(PolicyAssignment);
